import { useMutation, useQuery } from '@tanstack/react-query';
import { projectsApi } from './projects.api';
import { queryKeys } from '../queryKeys';
import type {
  CreateProjectPayload,
  UpdateProjectPayload,
  ProjectListParams,
} from '@clarix/shared-types';
import { toast } from '@clarix/ui';
import { getApiErrorMessage } from '@clarix/utils';
import { getRunningTimersConflict } from './archive-conflict';
import { emitProjectsChanged } from './projects-signal';

/** Projects list for dropdowns (e.g. tag condition scope). */
export function useProjectsList(params?: ProjectListParams, options?: { enabled?: boolean }) {
  const listParams = params ?? {};
  return useQuery({
    queryKey: queryKeys.projects.list(listParams as Record<string, unknown>),
    queryFn: () => projectsApi.list(listParams),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/** Tells the shell sidebar to refetch; the calling page still refreshes its own list. */
function useRefetchProjects() {
  return emitProjectsChanged;
}

export function useCreateProject() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: (data: CreateProjectPayload) => projectsApi.create(data),
    onSuccess: () => {
      toast.success('Project created successfully');
      refetch();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to create project'));
    },
  });
}

export function useUpdateProject() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectPayload }) =>
      projectsApi.update(id, data),
    onSuccess: () => {
      toast.success('Project updated successfully');
      refetch();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to update project'));
    },
  });
}

export function useUpdateSidebarProject() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectPayload }) =>
      projectsApi.update(id, data),
    onSuccess: () => {
      toast.success('Project updated');
      emitProjectsChanged();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to update project'));
    },
  });
}

export function useDeleteProject() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: (id: string) => projectsApi.delete(id),
    onSuccess: () => {
      toast.success('Project deleted');
      refetch();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to delete project'));
    },
  });
}

/**
 * Bulk soft-delete. The server applies the whole batch atomically or rejects it,
 * so there is no partial-success state to report.
 *
 * The CALLER must refresh its own list after this resolves; `useRefetchProjects`
 * only notifies the shell sidebar.
 */
export function useBulkDeleteProjects() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: (ids: string[]) => projectsApi.bulkDelete(ids),
    onSuccess: (result) => {
      toast.success(`${result.count} project${result.count === 1 ? '' : 's'} deleted`);
      refetch();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to delete projects'));
    },
  });
}

/**
 * Bulk is_active toggle. Same atomicity and same refetch caveat as
 * `useBulkDeleteProjects`.
 */
export function useBulkSetProjectsActive() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      projectsApi.bulkSetActive(ids, isActive),
    onSuccess: (result, variables) => {
      const verb = variables.isActive ? 'activated' : 'deactivated';
      toast.success(`${result.count} project${result.count === 1 ? '' : 's'} ${verb}`);
      refetch();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to update projects'));
    },
  });
}

/**
 * Archive a single project. Same refetch caveat as the bulk hooks.
 *
 * A `PROJECT_HAS_RUNNING_TIMERS` 409 is deliberately NOT toasted — it means
 * the caller should open the Force Archive dialog and re-fire with
 * `force: true`. Toasting it would both duplicate the dialog's message and
 * read as a failure when it is really a confirmation prompt.
 */
export function useArchiveProject() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      projectsApi.archive(id, force ?? false),
    onSuccess: () => {
      toast.success('Project archived');
      refetch();
    },
    onError: (err) => {
      if (getRunningTimersConflict(err)) return;
      toast.error(getApiErrorMessage(err, 'Failed to archive project'));
    },
  });
}

export function useRestoreProject() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: (id: string) => projectsApi.restore(id),
    onSuccess: () => {
      toast.success('Project restored');
      refetch();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to restore project'));
    },
  });
}

/** Bulk archive. Same force/409 contract as `useArchiveProject`. */
export function useBulkArchiveProjects() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: ({ ids, force }: { ids: string[]; force?: boolean }) =>
      projectsApi.bulkArchive(ids, force ?? false),
    onSuccess: (result) => {
      toast.success(`${result.count} project${result.count === 1 ? '' : 's'} archived`);
      refetch();
    },
    onError: (err) => {
      if (getRunningTimersConflict(err)) return;
      toast.error(getApiErrorMessage(err, 'Failed to archive projects'));
    },
  });
}

export function useBulkRestoreProjects() {
  const refetch = useRefetchProjects();
  return useMutation({
    mutationFn: (ids: string[]) => projectsApi.bulkRestore(ids),
    onSuccess: (result) => {
      toast.success(`${result.count} project${result.count === 1 ? '' : 's'} restored`);
      refetch();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to restore projects'));
    },
  });
}
